import { CountryCode, Currency, isCurrency, isSupportedLanguage, isCurrencyCountry } from './countries'
import { z } from 'zod'
import { GameId } from './game'
import { isCountryCode } from '../utils/countries'
import {
  PaymentProvider,
  RazerPaymentChannelIdentifier,
  TaxType,
  AdyenPaymentProvider,
  XsollaPaymentProvider as XsollaPaymentProviderSchema,
  RazerPaymentProvider,
} from './checkout'
import { ProductV2 } from './product'
import type { PaymentResponse } from '@adyen/api-library/lib/src/typings/checkout/paymentResponse'
import { EligibilityStatus } from './scid'
import type { CreateCheckoutSessionResponse } from '@adyen/api-library/lib/src/typings/checkout/createCheckoutSessionResponse'
import type { DistributiveOmit } from '../utils/distributiveOmit'

export enum PURCHASE_STATUS {
  AUTHORISED_WEBHOOK = 'AUTHORISATION',
  REFUND_WEBHOOK = 'REFUND',
  CHARGEBACK_WEBHOOK = 'CHARGEBACK',
  FAILED_CUSTOM = 'FAILED',
  STORE_DRAFT = 'STORE_DRAFT',
  STORE_PENDING = 'STORE_PENDING',
}

export type ReceiptIdentifier = 'id' | 'pspReference'
export interface PurchaseResponse {
  country: CountryCode
  creatorBoostCode: string | undefined
  // currency in which the actual payment is made
  currency: Currency
  game: GameId
  id: string
  email: string
  initializedTimestamp: number
  issuerCountry: string | undefined
  paymentMethod: string | undefined
  paymentMethodName: string | undefined
  paymentProvider: PaymentProvider
  processedTimestamp: number | undefined
  productId: string
  productPrice: number
  productTitle: string
  pspReference: string | undefined
  quantity: number
  status: string
  taxType: TaxType | undefined
  taxAmount: number | undefined
  taxPercentage: number | undefined
  priceBeforeTax: number | undefined
  total: number | undefined
  totalUSD: number | undefined
  receiptIdentifier: ReceiptIdentifier

  // original values that are used to initialize payments
  // Depends on the paymentProvider
  // For xsolla total, and currency might change due to currency changing while in payment
  // for adyen total and currency are the same as what user initialised the payment with
  checkoutTotal: number
  checkoutCurrency: Currency
}

const CurrencySchema = z.string().refine(isCurrency)
const VAT = z.literal('VAT')
const SalesTax = z.literal('SALES_TAX')
const TaxTypeSchema = VAT.or(SalesTax)
const PriceNumberSchema = z.number().int().gte(0)

const CheckoutPriceSchema = z.object({
  total: PriceNumberSchema,
  currency: CurrencySchema,
  currencyCountry: z.string().refine(isCurrencyCountry),
})

const PaymentPriceSchema = z.object({
  subtotal: PriceNumberSchema,
  taxAmount: PriceNumberSchema,
  total: PriceNumberSchema,
  currency: CurrencySchema,
  taxPercentage: z.number(),
  taxType: TaxTypeSchema,
})

const AnalyticsPriceSchema = z.object({
  total: PriceNumberSchema,
  currency: z.literal('USD'),
  rows: z.array(z.object({ sku: z.string(), total: PriceNumberSchema })).default([]),
})
const AdyenPurchasePricingSchema = z.object({
  paymentProvider: AdyenPaymentProvider,
  checkout: CheckoutPriceSchema,
  payment: PaymentPriceSchema,
  analytics: AnalyticsPriceSchema,
})

const XsollaPurchasePricingBaseSchema = z.object({
  paymentProvider: XsollaPaymentProviderSchema,
  checkout: CheckoutPriceSchema,
})

const XsollaPricingPreTaxSchema = XsollaPurchasePricingBaseSchema.extend({
  analytics: z.undefined(),
  payment: z.undefined().or(z.object({ total: PriceNumberSchema, currency: CurrencySchema.or(z.undefined()) })),
})

const XsollaPricingAfterTaxSchema = z
  .object({
    analytics: AnalyticsPriceSchema,
    payment: PaymentPriceSchema,
  })
  .merge(XsollaPurchasePricingBaseSchema)

const RazerPurchasePricingSchema = z.object({
  paymentProvider: RazerPaymentProvider,
  checkout: CheckoutPriceSchema,
  payment: PaymentPriceSchema.pick({ total: true, currency: true }),
  analytics: AnalyticsPriceSchema,
})

export const PurchaseRowSchema = z.object({
  sku: z.string(),
  quantity: z.number().int().gte(1),
})

const isPurchaseStatus = (s: string): s is PURCHASE_STATUS =>
  [
    PURCHASE_STATUS.STORE_DRAFT,
    PURCHASE_STATUS.STORE_PENDING,
    PURCHASE_STATUS.AUTHORISED_WEBHOOK,
    PURCHASE_STATUS.REFUND_WEBHOOK,
    PURCHASE_STATUS.CHARGEBACK_WEBHOOK,
    PURCHASE_STATUS.FAILED_CUSTOM,
  ]
    .map((status) => status.toString())
    .includes(s)

const PurchaseStatusSchema = z.string().refine(isPurchaseStatus)

export const PurchaseBaseSchema = z.object({
  id: z.string(),
  customerId: z.string(),
  customerIP: z.string(),
  status: PurchaseStatusSchema,
  rows: z.array(PurchaseRowSchema),
  email: z.string(),
  language: z.string().refine(isSupportedLanguage),
  // Creator Boost
  creatorBoost: z
    .object({
      value: z.string(),
      scid: z.string(),
    })
    .or(z.null()),
  // Discount Codes
  discountCodes: z.array(z.object({ value: z.string(), id: z.string() })),

  country: z.string().refine(isCountryCode),

  revision: z.number().int().gte(0),
  createdAt: z.number().int().positive(),
  updatedAt: z.number().int().positive(),
  migratedAt: z.number().positive().optional(), // TODO: should this be optional

  version: z.literal('v0'),
})

const PurchaseDraftCommonFields = z.object({
  status: z.literal(PURCHASE_STATUS.STORE_DRAFT),
  issuerCountry: z.string().optional(),
  paymentMethod: z.string().optional(),
  paymentMethodName: z.string().optional(),
  sessionContentHash: z.string().optional(),
})

const AdyenPurchaseBaseSchema = PurchaseBaseSchema.merge(AdyenPurchasePricingSchema).extend({
  paymentData: z.string().optional(),
})
export const AdyenPurchaseDraftSchema = AdyenPurchaseBaseSchema.merge(PurchaseDraftCommonFields)
export const XsollaPurchaseDraftSchema =
  PurchaseBaseSchema.merge(PurchaseDraftCommonFields).merge(XsollaPricingPreTaxSchema)

const RazerPurchaseBaseSchema = PurchaseBaseSchema.merge(RazerPurchasePricingSchema)
export const RazerPurchaseDraftSchema = RazerPurchaseBaseSchema.merge(PurchaseDraftCommonFields)

export const PurchaseDraftSchema = z.discriminatedUnion('paymentProvider', [
  AdyenPurchaseDraftSchema,
  XsollaPurchaseDraftSchema,
  RazerPurchaseDraftSchema,
])

export const AdyenPurchasePendingSchema = AdyenPurchaseBaseSchema.extend({
  status: z.literal(PURCHASE_STATUS.STORE_PENDING),
  issuerCountry: z.string().optional(),
  paymentMethod: z.string().optional(),
  paymentMethodName: z.string().optional(),
}).merge(AdyenPurchasePricingSchema)

const XsollaPurchasePendingSchema = PurchaseBaseSchema.extend({
  status: z.literal(PURCHASE_STATUS.STORE_PENDING),
  paymentMethodName: z.string(),
}).merge(XsollaPricingPreTaxSchema)

export const RazerPurchasePendingSchema = PurchaseBaseSchema.extend({
  status: z.literal(PURCHASE_STATUS.STORE_PENDING),
  paymentMethod: RazerPaymentChannelIdentifier,
  paymentMethodName: z.string().optional(),
  pspReference: z.string(),
}).merge(RazerPurchasePricingSchema)

const PurchaseCompletedCommonFields = z.object({
  issuerCountry: z.string().optional(),
  paymentMethod: z.string(),
  paymentMethodName: z.string().optional(),
  pspReference: z.string(),
  processedTimestamp: z.number(),
  itemsReceived: z.boolean(),
})

const PurchaseAuthorisedCommonFields = PurchaseCompletedCommonFields.extend({
  status: z.literal(PURCHASE_STATUS.AUTHORISED_WEBHOOK),
  refundTriggered: z.boolean().optional(),
})

export const AdyenPurchaseAuthorisedSchema =
  PurchaseBaseSchema.merge(PurchaseAuthorisedCommonFields).merge(AdyenPurchasePricingSchema)
export const XsollaPurchaseAuthorisedSchema =
  PurchaseBaseSchema.merge(PurchaseAuthorisedCommonFields).merge(XsollaPricingAfterTaxSchema)
export const RazerPurchaseAuthorizedSchema = PurchaseBaseSchema.merge(PurchaseAuthorisedCommonFields)
  .merge(z.object({ paymentMethod: RazerPaymentChannelIdentifier }))
  .merge(RazerPurchasePricingSchema)

export const AdyenPurchaseFailedSchema = PurchaseBaseSchema.extend({
  status: z.literal(PURCHASE_STATUS.FAILED_CUSTOM),
  issuerCountry: z.string().optional(),
  paymentMethod: z.string().optional(),
  paymentMethodName: z.string().optional(),
  pspReference: z.string().optional(),
  processedTimestamp: z.number(),
}).merge(AdyenPurchasePricingSchema)

const XsollaPurchaseFailedCommonFields = z.object({
  status: z.literal(PURCHASE_STATUS.FAILED_CUSTOM),
  issuerCountry: z.string().optional(),
  paymentMethod: z.string().optional(),
  paymentMethodName: z.string().optional(),
  pspReference: z.string().optional(),
  processedTimestamp: z.number(),
})

const RazerPurchaseFailedSchema = PurchaseBaseSchema.extend({
  status: z.literal(PURCHASE_STATUS.FAILED_CUSTOM),
  paymentMethod: RazerPaymentChannelIdentifier,
  paymentMethodName: z.string().optional(),
  pspReference: z.string(),
  processedTimestamp: z.number(),
}).merge(RazerPurchasePricingSchema)

const XsollaFailedPreTax = PurchaseBaseSchema.merge(XsollaPricingPreTaxSchema).merge(XsollaPurchaseFailedCommonFields)
const XsollaFailedAfterTax = PurchaseBaseSchema.merge(XsollaPricingAfterTaxSchema).merge(
  XsollaPurchaseFailedCommonFields,
)

export const XsollaPurchaseFailedSchema = XsollaFailedAfterTax.or(XsollaFailedPreTax)

export type AdyenPurchaseFailed = z.infer<typeof AdyenPurchaseFailedSchema>

export const PurchaseAuthorisedSchema =
  AdyenPurchaseAuthorisedSchema.or(XsollaPurchaseAuthorisedSchema).or(RazerPurchaseAuthorizedSchema)

const PurchaseRefundedCommonFields = PurchaseCompletedCommonFields.extend({
  status: z.literal(PURCHASE_STATUS.REFUND_WEBHOOK),
  itemsRevoked: z.boolean(),
  refundReason: z.string().optional(),
})

const PurchaseChargebackedCommonFields = PurchaseCompletedCommonFields.extend({
  status: z.literal(PURCHASE_STATUS.CHARGEBACK_WEBHOOK),
  itemsRevoked: z.boolean(),
})

export const AdyenPurchaseRefundedSchema =
  PurchaseBaseSchema.merge(PurchaseRefundedCommonFields).merge(AdyenPurchasePricingSchema)
export const AdyenPurchaseChargebackedSchema = PurchaseBaseSchema.merge(PurchaseChargebackedCommonFields).merge(
  AdyenPurchasePricingSchema,
)

export const XsollaPurchaseRefundedSchema =
  PurchaseBaseSchema.merge(PurchaseRefundedCommonFields).merge(XsollaPricingAfterTaxSchema)
export const XsollaPurchaseChargebackedSchema = PurchaseBaseSchema.merge(PurchaseChargebackedCommonFields).merge(
  XsollaPricingAfterTaxSchema,
)

// Would be faster with discriminated unions, but it does not seem to be supported for this?
export const PurchaseSchema = z.union([
  AdyenPurchaseDraftSchema,
  AdyenPurchasePendingSchema,
  AdyenPurchaseFailedSchema,
  AdyenPurchaseAuthorisedSchema,
  AdyenPurchaseRefundedSchema,
  AdyenPurchaseChargebackedSchema,
  XsollaPurchaseDraftSchema,
  XsollaPurchasePendingSchema,
  XsollaPurchaseAuthorisedSchema,
  XsollaPurchaseFailedSchema,
  XsollaPurchaseRefundedSchema,
  XsollaPurchaseChargebackedSchema,
  RazerPurchaseDraftSchema,
  RazerPurchasePendingSchema,
  RazerPurchaseAuthorizedSchema,
])

const RefundedPurchaseSchema = XsollaPurchaseRefundedSchema.or(AdyenPurchaseRefundedSchema)
const CharegebackedPurchaseSchema = XsollaPurchaseChargebackedSchema.or(AdyenPurchaseChargebackedSchema)
export const PurchasePendingSchema =
  XsollaPurchasePendingSchema.or(AdyenPurchasePendingSchema).or(RazerPurchasePendingSchema)
export type PurchaseDraft = z.infer<typeof PurchaseDraftSchema>
export type PurchasePending = z.infer<typeof PurchasePendingSchema>
export type PurchaseAdyenDraft = z.infer<typeof AdyenPurchaseDraftSchema>
export type PurchaseAdyenPending = z.infer<typeof AdyenPurchasePendingSchema>
export type PurchaseAdyenAuthorised = z.infer<typeof AdyenPurchaseAuthorisedSchema>
export type PurchaseAdyenChargeback = z.infer<typeof AdyenPurchaseChargebackedSchema>
export type PurchaseAdyenRefund = z.infer<typeof AdyenPurchaseRefundedSchema>
export type PurchaseAdyenFailed = z.infer<typeof AdyenPurchaseFailedSchema>

export type PurchaseXsollaDraft = z.infer<typeof XsollaPurchaseDraftSchema>
export type PurchaseXsollaPending = z.infer<typeof XsollaPurchasePendingSchema>
export type PurchaseXsollaFailed = z.infer<typeof XsollaPurchaseFailedSchema>
export type PurchaseXsollaAuthorised = z.infer<typeof XsollaPurchaseAuthorisedSchema>
export type PurchaseXsollaRefund = z.infer<typeof XsollaPurchaseRefundedSchema>
export type PurchaeXsollaChargebacked = z.infer<typeof XsollaPurchaseChargebackedSchema>
export type PurchaseAuthorised = z.infer<typeof PurchaseAuthorisedSchema>

export type XsollaPricingPreTax = z.infer<typeof XsollaPricingPreTaxSchema>
export type XsollaPricingAfterTax = z.infer<typeof XsollaPricingAfterTaxSchema>
export type AdyenPricing = z.infer<typeof AdyenPurchasePricingSchema>
export type RazerPricing = z.infer<typeof RazerPurchasePricingSchema>

export type PurchaseRazerDraft = z.infer<typeof RazerPurchaseDraftSchema>
export type PurchaseRazerPending = z.infer<typeof RazerPurchasePendingSchema>
export type PurchaseRazerAuthorized = z.infer<typeof RazerPurchaseAuthorizedSchema>
export type PurchaseRazerFailed = z.infer<typeof RazerPurchaseFailedSchema>

export type AdyenPurchase =
  | PurchaseAdyenDraft
  | PurchaseAdyenPending
  | PurchaseAdyenAuthorised
  | PurchaseAdyenChargeback
  | PurchaseAdyenRefund
  | PurchaseAdyenFailed

export type XsollaPurchase =
  | PurchaseXsollaDraft
  | PurchaseXsollaPending
  | PurchaseXsollaFailed
  | PurchaseXsollaAuthorised
  | PurchaseXsollaRefund
  | PurchaeXsollaChargebacked

export type RazerPurchase = PurchaseRazerDraft | PurchaseRazerPending | PurchaseRazerAuthorized | PurchaseRazerFailed

export type Purchase = AdyenPurchase | XsollaPurchase | RazerPurchase

export type RefundedPurchase = z.infer<typeof RefundedPurchaseSchema>
export type ChargebackPurchase = z.infer<typeof CharegebackedPurchaseSchema>
export type PendingPurchase = z.infer<typeof PurchasePendingSchema>

type PurchaseRowWithProducts = { quantity: number; product: ProductV2 }

export type PurchaseRowWithProductsAndEligibilities = PurchaseRowWithProducts & { eligibility: EligibilityStatus }

export type PurchaseDraftWithProducts<T extends PurchaseDraft = PurchaseDraft> = DistributiveOmit<
  T,
  'rows' | 'creatorBoost'
> & {
  rows: PurchaseRowWithProductsAndEligibilities[]
  creatorBoost: null | string
}

export type AdyenPurchaseDraftWithProducts = PurchaseDraftWithProducts<PurchaseAdyenDraft>
export type XsollaPurchaseDraftWithProducts = PurchaseDraftWithProducts<PurchaseXsollaDraft>
export type RazerPurchaseDraftWithProducts = PurchaseDraftWithProducts<PurchaseRazerDraft>

export type PurchaseWithProducts = DistributiveOmit<Purchase, 'rows' | 'creatorBoost'> & {
  rows: PurchaseRowWithProducts[]
  creatorBoost: string
}
export type PurchaseGetDraftResponse = PurchaseDraftWithProducts | null

export const PurchasesUpdateDraftInputSchema = z.object({
  rows: z.array(z.object({ sku: z.string(), quantity: z.number().int().positive() })).optional(),
  countryCode: z.string().refine(isCountryCode),
  language: z.string().refine(isSupportedLanguage),
  creatorBoostCode: z.string().optional(),
  paymentProvider: PaymentProvider.optional(),
})

export type PurchasesUpdateDraftRequest = z.infer<typeof PurchasesUpdateDraftInputSchema>
export type PurchasesUpdateDraftResponse = PurchaseDraftWithProducts

export const CheckoutSessionInputSchema = z.object({
  id: z.string(),
  revision: z.number(),
})

export const RazerCheckoutSessionInputSchema = z
  .object({
    paymentChannel: RazerPaymentChannelIdentifier,
  })
  .merge(CheckoutSessionInputSchema)

export type CheckoutSessionRequest = z.infer<typeof CheckoutSessionInputSchema>
export type RazerCheckoutSessionInputSchema = z.infer<typeof RazerCheckoutSessionInputSchema>

type CheckoutNullSessionResponse<T> = {
  session: null
  inEligible: {
    [key: string]: EligibilityStatus
  }
  paymentProvider: T
}

export type CheckoutAdyenSessionResponse =
  | {
      session: CreateCheckoutSessionResponse & {
        countryCode: CountryCode
      }
      inEligible: null
      paymentProvider: 'ADYEN'
      id: string
      revision: number
      updatedAt: number
    }
  | CheckoutNullSessionResponse<'ADYEN'>

export type CheckoutXsollaSessionResponse =
  | {
      session: string
      inEligible: null
      paymentProvider: 'XSOLLA'
    }
  | CheckoutNullSessionResponse<'XSOLLA'>

export type CheckoutRazerSessionResponse =
  | {
      session: {
        paymentUrl: string
      }
      inEligible: null
      paymentProvider: 'RAZER_GOLD'
    }
  | CheckoutNullSessionResponse<'RAZER_GOLD'>

export type CheckoutSessionResponse =
  | CheckoutAdyenSessionResponse
  | CheckoutXsollaSessionResponse
  | CheckoutRazerSessionResponse

export const CheckoutAdyenPaymentRequestSchema = z.object({
  id: z.string(),
  revision: z.number(),
  paymentMethodName: z.string(),
  data: z.any(),
})

export type CheckoutAdyenPaymentRequest = z.infer<typeof CheckoutAdyenPaymentRequestSchema>
export type CheckoutAdyenPaymentResponse = {
  id: string
  revision: number
  paymentResponse: PaymentResponse
}

export const CheckoutAdyenPaymentDetailsRequestSchema = z.object({
  details: z.object({}).passthrough(),
  paymentData: z.string().optional(),
  paymentMethodName: z.string(),
  id: z.string(),
  revision: z.number(),
})

export type CheckoutAdyenPaymentDetailsRequest = z.infer<typeof CheckoutAdyenPaymentDetailsRequestSchema>
export type CheckoutAdyenPaymentDetailsResponse = {
  id: string
  resultCode: PaymentResponse['resultCode']
  action: PaymentResponse['action']
}

const PurchasesGetInputSchema = z.object({ id: z.string() })
export type PurchasesGetRequest = z.infer<typeof PurchasesGetInputSchema>
export type PurchasesGetResponse = Omit<Purchase, 'rows' | 'creatorBoost'> & {
  rows: PurchaseRowWithProducts[]
  creatorBoost: string
}

export type PurchasesGetPendingReponse = Omit<PurchasePending, 'creatorBoost'> & {
  creatorBoost: string
}

export type PandoraPurchaseResponse = {
  timestamp: string // ISO Timestamp
  scid: string
  status: PURCHASE_STATUS
  transactionId: string // Store purchase id
  source: 'store'
  totalLocal: {
    currency: Currency
    amount: number // Major units
  }
  totalUSD: number
  rows: {
    [sku: string]: {
      game: GameId | ''
      quantity: number
      title?: string
      totalUSD: number // major units multiplied by quantity
    }
  }
}

export const PURCHASE_DRAFT_MAX_SKU_COUNT = 30
export const MAX_ROW_SKU_COUNT = 10
